import React, { useState, useEffect } from 'react';
import { fetchSubcounties } from '../services/subcountyService';
import { fetchSubCountyAnalysis } from '../services/analysisService'; // Added for lifting state
import SubCountyComparisonTab from '../components/SubCountyComparisonTab';
import SubCountyCurrentTab from '../components/SubCountyCurrentTab';
import { exportToPDF } from '../utils/pdfExportUtils';
import { FileSpreadsheet, FileDown } from 'lucide-react';
import { exportToExcel } from '../utils/excelExportUtils';
import { useAuth } from '../context/useAuth';

const SubCountyAnalysis = () => {
  const {  user } = useAuth();
  const [subcounties, setSubcounties] = useState([]);
  // const [selectedId, setSelectedId] = useState('');
  const [selectedId, setSelectedId] = useState(user.sub_county_id || ''); // Pre-select if user is restricted
  const [activeTab, setActiveTab] = useState('current'); // 'current' or 'comparison'
  const [reportData, setReportData] = useState({}); // Local state to hold data for exports
  const [loading, setLoading] = useState(false);
  

  const now = new Date();
  const fyStart = now.getMonth() + 1 >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  const subCountyName = subcounties.find(sc => sc.id == selectedId)?.name || 'Sub-County';
  const subCountyRO = subcounties.find(sc => sc.id == selectedId)?.username || 'Not Assigned';
  useEffect(() => {
    fetchSubcounties().then(data => {
      setSubcounties(data);
      // Double check: if restricted, ensure the ID is locked in even if 
      // the dropdown list took a moment to load
    });
  }, []);
  
  // Lifted Data Fetching Logic
  useEffect(() => {
    if (selectedId) {
      setLoading(true);
      fetchSubCountyAnalysis(fyStart, selectedId).then(res => {
        const grouped = res.reduce((acc, row) => {
          if (!acc[row.rev_stream]) acc[row.rev_stream] = [];
          acc[row.rev_stream].push(row);
          return acc;
        }, {});
        setReportData(grouped);
        setLoading(false);
      });
    }
  }, [selectedId, fyStart]);

   const handleExcelExport = () => {
    const subCountyName = subcounties.find(sc => sc.id == selectedId)?.name || 'Sub-County';
    const excelData = [];
    Object.entries(reportData).map(([stream, months]) => {
      const actual = months.reduce((s, m) => s + (m.amount_fy0 || 0), 0);
      const target = months[0]?.annual_target || 0;
      const performance = target > 0 ? (actual / target) : 0;

      if (activeTab === 'comparison') {
		//Annual Data
        excelData.push({
          'Revenue Stream': stream,
          [`FY ${fyStart - 2}/${fyStart - 1}`]: months.reduce((s, m) => s + (m.amount_fy_minus_2 || 0), 0),
          [`FY ${fyStart - 1}/${fyStart}`]: months.reduce((s, m) => s + (m.amount_fy_minus_1 || 0), 0),
          [`Current FY ${fyStart}/${fyStart + 1}`]: actual,
          'Annual-Monthly Target': target,
          'Performance %': (performance * 100).toFixed(1) + '%',
          'Status': 'Annual'
        });
		//Monthly Data
		months.forEach(m => {
        const targetKey = `${m.calendar_month.substring(0, 3).toLowerCase()}_target`;
		const monthlyActual = m.amount_fy0;
		const monthlyTarget = m.monthly_target || 0;
		const monthlyPerfom = monthlyTarget > 0? (monthlyActual / monthlyTarget) : 0;
        excelData.push({
          'Revenue Stream': ` => ${m.calendar_month}`,
		      [`FY ${fyStart - 2}/${fyStart - 1}`]: months.reduce((s, m) => s + (m.amount_fy_minus_2 || 0), 0),
          [`FY ${fyStart - 1}/${fyStart}`]: months.reduce((s, m) => s + (m.amount_fy_minus_1 || 0), 0),
          [`Current FY ${fyStart}/${fyStart + 1}`]: monthlyActual,
          'Annual-Monthly Target': monthlyTarget,
		      'Performance %': (monthlyPerfom * 100).toFixed(1) + '%',
          'Status': 'Monthly'
        });
      });
      }
	 else {
		//Annual Data
        excelData.push({
          'Revenue Stream': stream,
          [`Current FY ${fyStart}/${fyStart + 1}`]: actual,
          'Annual-Monthly Target': target,
          'Performance %': (performance * 100).toFixed(1) + '%',
		      'Status': 'Annual'
        });
		//Monthly Data
		months.forEach(m => {
        const targetKey = `${m.calendar_month.substring(0, 3).toLowerCase()}_target`;
		const monthlyActual = m.amount_fy0;
		const monthlyTarget = m.monthly_target || 0;
		const monthlyPerfom = monthlyTarget > 0? (monthlyActual / monthlyTarget) : 0;
        excelData.push({
          'Revenue Stream': ` => ${m.calendar_month}`,
		      [`Current FY ${fyStart}/${fyStart + 1}`]: monthlyActual,
          'Annual-Monthly Target': monthlyTarget,
		      'Performance %': (monthlyPerfom * 100).toFixed(1) + '%',
          'Status': 'Monthly'
        });
      });
      }
    });

    // Calculate Totals for the Grand Total Row
    const totalFy0 = Object.values(reportData).flat().reduce((s, m) => s + (m.amount_fy0 || 0), 0);
    const totalTarget = Object.values(reportData).reduce((s, m) => s + (m[0]?.annual_target || 0), 0);
    const totalPerf = totalTarget > 0 ? (totalFy0 / totalTarget) : 0;

    let grandTotalRow = { 'Revenue Stream': 'GRAND TOTAL' };
    
    if (activeTab === 'comparison') {
      grandTotalRow = {
        ...grandTotalRow,
        [`FY ${fyStart - 2}/${fyStart - 1}`]: Object.values(reportData).flat().reduce((s, m) => s + (m.amount_fy_minus_2 || 0), 0),
        [`FY ${fyStart - 1}/${fyStart}`]: Object.values(reportData).flat().reduce((s, m) => s + (m.amount_fy_minus_1 || 0), 0),
        [`Current FY ${fyStart}/${fyStart + 1}`]: totalFy0,
        'Annual-Monthly Target': totalTarget,
        'Performance %': (totalPerf * 100).toFixed(1) + '%',
        'Status': 'Grand Annual'
      };
    } else {
      grandTotalRow = {
        ...grandTotalRow,
        [`Current FY ${fyStart}/${fyStart + 1}`]: totalFy0,
        'Annual-Monthly Target': totalTarget,
        'Performance %': (totalPerf * 100).toFixed(1) + '%',
        'Status': 'Grand Annual'
      };
    }

    excelData.push(grandTotalRow);

    exportToExcel(
      excelData, 
      `${subCountyName}_${activeTab}_Revenue_Analysis_${fyStart}.xlsx`, 
      subCountyName
    );
  };
  const handleDownloadPDF = () => {
    const subCountyName = subcounties.find(sc => sc.id == selectedId)?.name || 'Sub-County';
    const title = `${subCountyName} Revenue Analysis - ${activeTab === 'comparison' ? '3-Year Comparison' : 'Current FY'}`;
    
    const headers = activeTab === 'comparison' 
      ? ['Revenue Stream', `FY ${fyStart-2}/${fyStart-1}`, `FY ${fyStart-1}/${fyStart}`, `FY ${fyStart}/${fyStart+1}`, 'Annual-Monthly Current Target', 'Performance %']
      : ['Revenue Stream', `FY ${fyStart}/${fyStart+1}`, 'Annual-Monthly Current Target', 'Performance %'];

    const tableBody = [];
    Object.entries(reportData).forEach(([stream, months]) => {
      const actual = months.reduce((s, m) => s + (m.amount_fy0 || 0), 0);
      const target = months.find(m => m.annual_target > 0)?.annual_target || 0;
      const perf = target > 0 ? ((actual / target) * 100).toFixed(1) + '%' : '0.0%';

      if (activeTab === 'comparison') {
        const fy1 = months.reduce((s, m) => s + (m.amount_fy_minus_1 || 0), 0);
        const fy2 = months.reduce((s, m) => s + (m.amount_fy_minus_2 || 0), 0);
        tableBody.push([stream, fy2.toLocaleString(), fy1.toLocaleString(), actual.toLocaleString(), target.toLocaleString(), perf]);
      } else {
        tableBody.push([stream, actual.toLocaleString(), target.toLocaleString(), perf]);
      }

      // Monthly expansion for PDF
      months.forEach(m => {
        const targetKey = `${m.calendar_month.substring(0, 3).toLowerCase()}_target`;
		const mActual = m.amount_fy0 || 0;
        const mTarget = m.monthly_target || 0;
		const mPerf = mTarget > 0 ? ((mActual / mTarget) * 100).toFixed(1) + '%' : '0.0%';
        const row = activeTab === 'comparison' 
          ? [`  > ${m.calendar_month}`, m.amount_fy_minus_2.toLocaleString(), m.amount_fy_minus_1.toLocaleString(), mActual.toLocaleString(), mTarget.toLocaleString(), mPerf]
          : [`  > ${m.calendar_month}`, mActual.toLocaleString(), mTarget.toLocaleString(), mPerf];
        tableBody.push(row);
      });
    });
	
	// Add Grand Total to PDF
    const totalFy0 = Object.values(reportData).flat().reduce((s, m) => s + (m.amount_fy0 || 0), 0);
    const totalTarget = Object.values(reportData).reduce((s, m) => s + (m[0]?.annual_target || 0), 0);
    const totalPerf = totalTarget > 0 ? ((totalFy0 / totalTarget) * 100).toFixed(1) + '%' : '0.0%';

    if (activeTab === 'comparison') {
        const totalFyMinus1 = Object.values(reportData).flat().reduce((s, m) => s + (m.amount_fy_minus_1 || 0), 0);
        const totalFyMinus2 = Object.values(reportData).flat().reduce((s, m) => s + (m.amount_fy_minus_2 || 0), 0);
        tableBody.push(['GRAND TOTAL', totalFyMinus2.toLocaleString(), totalFyMinus1.toLocaleString(), totalFy0.toLocaleString(), totalPerf]);
    } else {
        tableBody.push(['GRAND TOTAL', totalFy0.toLocaleString(), totalTarget.toLocaleString(), totalPerf]);
    }
    exportToPDF(title, headers, tableBody, `${subCountyName}_${activeTab}_Analysis.pdf`);
  };
  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl border shadow-sm flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Sub-County Analysis</h2>
          {selectedId && (
            <p className="text-sm font-semibold text-blue-500 italic">
              Viewing analysis for {subCountyName} <span className = "text-red-500"> (RO: {subCountyRO})</span>
            </p>
          )}
          {!selectedId && (
            <p className="text-sm font-medium text-gray-500">
              Please select a sub-county to view detailed analysis.
            </p>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {selectedId && Object.keys(reportData).length > 0 && (
            <div className="flex items-center space-x-2">
              <button 
                onClick={handleDownloadPDF}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-sm font-bold shadow-sm"
              >
                <FileDown size={16} /> Export PDF
              </button>
              {/*
              <button 
                onClick={handleExcelExport}
                className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all text-sm font-bold shadow-sm"
              >
                <FileSpreadsheet size={18} className="mr-2" /> Excel
              </button>
              */}
            </div>
          )}
          <select 
            className="border border-gray-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={user.isRestricted} 
          >
            <option value="">Select Sub-County</option>
            {subcounties.map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
          </select>
        </div>
      </div>

      {selectedId ? (
        <div className="space-y-4">
          <div className="flex space-x-4 border-b">
            <button 
              onClick={() => setActiveTab('comparison')}
              className={`pb-2 px-4 font-medium transition-colors ${activeTab === 'comparison' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              3-Financial Year's Comparison
            </button>
            <button 
              onClick={() => setActiveTab('current')}
              className={`pb-2 px-4 font-medium transition-colors ${activeTab === 'current' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Current Financial Year Performance 
              <span className="text-xs font-bold text-red-500 ml-2">(FY {fyStart}/{fyStart + 1})</span>
            </button>
          </div>

          <div className="animate-fadeIn">
            {activeTab === 'comparison' ? (
              <SubCountyComparisonTab subCountyId={selectedId} fyStart={fyStart} data={reportData} loading={loading} />
            ) : (
              <SubCountyCurrentTab subCountyId={selectedId} fyStart={fyStart} data={reportData} loading={loading} />
            )}
          </div>
        </div>
      ) : (
        <div className="bg-blue-50 p-10 rounded-xl border border-blue-100 text-center text-blue-600 italic">
          Please select a sub-county from the dropdown above to view analysis data.
        </div>
      )}
    </div>
  );
};

export default SubCountyAnalysis;